// Agent chat UI component.
// Copied + factored from frontend/src/components/agent/AgentChat.tsx.
// Factoring changes:
//   - Removed InlinePlatinumTable, InlineDashboard, ArtifactViewer (Platinum render plugins).
//   - Removed AGENT_MODELS constant; model list is a prop.
//   - Removed PreparingToolCard; inline equivalent.
//   - ThinkingBlock extracted to its own component (ThinkingBlock.tsx).
//   - Plugin slots for table/chart/dashboard rendering via RenderPlugin seam.
//   - API base URL and auth header as props.
//   - No dependency on AccountContext, router5, or Platinum app state.
// See ../../docs/09-frontend-components.md and ../../docs/90-provenance-map.md.

import { useComposerDraft } from '../hooks/useComposerDraft.js'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Fab, Fade, Tooltip, Typography, Alert } from '@mui/material'
import type { ActivityStatus, AgentMessage, ArtifactInfo, AskUserQuestionInfo, CreatedDashboardInfo, RenderedTableInfo, RenderedChartInfo, TodoItem } from '../types.js'
import { getToolCategory, getToolIcon } from '../tool-formatters.js'
import ActivityBadge, { ACTIVITY_BADGE_SIZE } from './ActivityBadge.js'
import { composerStatusLine } from './AgentsSection.js'
import { composerButtonCentreInset, COMPOSER_BUTTON_SX, COMPOSER_OVERHANG_PX } from './ComposerCard.js'
import { useActivityLog } from '../hooks/useActivityLog.js'
import ArtifactPanel, { ARTIFACT_PANEL_WIDTH } from './ArtifactPanel.js'
import ArtifactPanelPreview, { PREVIEW_RAIL_WIDTH } from './ArtifactPanelPreview.js'
import EdgeReveal, { EDGE_PULSE_MS, EDGE_STRIP_WIDTH, EDGE_SLIDE_MS, EDGE_RETREAT_MS, EDGE_REVEAL_EASING, EDGE_RETREAT_EASING } from './EdgeReveal.js'
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { CARD_SX, HAIRLINE, HAIRLINE_COLOR } from './surface.js'
import { collapseBase64 } from '../base64.js'
import { fillAnswerDraft } from '../askUserAnswer.js'
import MessageRow from './MessageRow.js'
import ChatInputToolbar from './ChatInputToolbar.js'
import useFileAttachments from '../hooks/useFileAttachments.js'
import useVoiceDictation from '../hooks/useVoiceDictation.js'
import type { RenderPlugin, AgentSSEEvent, AgentComposerConfig, AgentChatSheetConfig, AgentComposerHandle } from '../plugins.js'
import type { AgentSSEEvent as CoreSSEEvent } from '../types.js'
import type { DeckAsk } from '../presentationFrame.js'
import { useAgentChatContextOptional } from '../AgentChatProvider.js'
import ComposerCard from './ComposerCard.js'
import { useRowSlices } from './useRowSlices.js'
import DelegatedAgentDrawer, { type AgentRef } from './DelegatedAgentDrawer.js'
import { DelegatedAgentsContext, type DelegatedAgentsApi } from './DelegatedAgentsContext.js'
import { deriveDelegatedAgents } from '../delegatedAgents.js'
import { createWorkflowAgentsSource, useWorkflowRuns, WorkflowAgentsSourceContext } from '../hooks/useWorkflowAgents.js'

/**
 * Render a relative-cost multiplier for the model picker: 2 -> "2x", 0.4 ->
 * "0.4x". Trailing zeros are trimmed so whole numbers read as "1x", not "1.0x".
 * The multiplier is relative to the host's baseline model, not a price.
 */
export { formatCostMultiplier } from './ComposerCard.js'

/**
 * Fold plugin events into per-plugin, per-toolCallId state maps.
 *
 * Pure — replay-safe: same input events always produce the same output.
 *
 * Returns Map<pluginIndex, Map<toolCallId, TState>>.
 * Keyed by plugin index so two plugins sharing an event type never collide.
 *
 * Accepts CoreSSEEvent (data: unknown from types.ts) so hosts can pass the
 * pluginEvents array returned directly from useAgentSession without a cast.
 * The data fields are accessed via an explicit cast inside.
 */
export function foldPluginEvents(
  plugins: RenderPlugin[],
  pluginEvents: CoreSSEEvent[],
): Map<number, Map<string, unknown>> {
  const byPlugin = new Map<number, Map<string, unknown>>()
  for (let i = 0; i < plugins.length; i++) {
    const plugin = plugins[i]
    const typeSet = new Set(plugin.eventTypes)
    const byTool = new Map<string, unknown>()
    for (const ev of pluginEvents) {
      if (!typeSet.has(ev.type)) continue
      // Cast data to Record so we can extract the toolCallId key. Plugin.reduce()
      // receives the event typed as plugins.AgentSSEEvent (data: Record<string,unknown>)
      // — the cast is safe because any event processed here went through the SSE parser
      // which always produces an object for data.
      const d = ev.data as Record<string, unknown>
      const key =
        (d?.toolCallId as string | undefined) ||
        (d?.tool_use_id as string | undefined) ||
        (d?.messageId as string | undefined) ||
        ''
      const pluginEv: AgentSSEEvent = { type: ev.type, data: d, timestamp: ev.timestamp }
      const prev = byTool.has(key) ? byTool.get(key) : plugin.init()
      byTool.set(key, plugin.reduce(prev, pluginEv))
    }
    byPlugin.set(i, byTool)
  }
  return byPlugin
}

/**
 * Words for a status row whose event arrived without a label. A spinner on its
 * own is not a status — the reader cannot tell work from a hang (#1294).
 */
export const STATUS_FALLBACK_LABEL = 'Working…'

/**
 * How long after one Escape a second Escape still counts as "stop this turn"
 * (#1295). One Escape on its own does nothing — a stray keypress must not end
 * a run the reader is watching.
 */
export const DOUBLE_ESCAPE_WINDOW_MS = 1500

interface AgentChatProps {
  // All props are optional so <AgentChat/> can be used inside <AgentChatProvider>
  // with zero props. Each value falls back to the context when omitted.
  messages?: AgentMessage[]
  /**
   * Messages sent while a turn was running, waiting for it to end. Drawn after
   * `messages`, each with a "Queued" marker. Falls back to the session's.
   */
  queuedMessages?: AgentMessage[]
  isStreaming?: boolean
  error?: string | null
  activityStatus?: ActivityStatus | null
  onSendMessage?: (content: string, model?: string, attachmentIds?: string[], effort?: string) => void
  onCancel?: () => void
  artifacts?: ArtifactInfo[]
  todos?: TodoItem[]
  renderedTables?: Map<string, RenderedTableInfo>
  renderedCharts?: Map<string, RenderedChartInfo>
  askedQuestions?: Map<string, AskUserQuestionInfo>
  createdDashboards?: Map<string, CreatedDashboardInfo>
  onAnswerQuestion?: (toolCallId: string, value: string) => void
  sessionId?: string
  toolInputBuffer?: string
  selectedModel?: string
  onModelChange?: (model: string) => void
  /** Model list for the model selector. */
  models?: { id: string; label: string; costMultiplier?: number }[]
  lastHeartbeat?: number
  stuckStatus?: 'ok' | 'possibly_stuck' | 'likely_stuck'
  onNudge?: () => void
  readOnly?: boolean
  /** Render plugins (e.g. Platinum's Carbon table/chart widgets). */
  plugins?: RenderPlugin[]
  /**
   * Raw plugin events accumulated by useAgentSession (live + restored).
   * Folded per-plugin per-toolCallId by AgentChat via foldPluginEvents.
   * Pass useAgentSession's `pluginEvents` return value here directly.
   */
  pluginEvents?: CoreSSEEvent[]
  /** API base URL for artifact downloads. */
  apiBaseUrl?: string
  /** Auth header for artifact downloads and voice transcription. */
  authHeader?: string
  /** Transcription endpoint. Default: apiBaseUrl + /transcribe */
  transcribeEndpoint?: string
  /** Optional upload endpoint factory for file attachments. */
  /** Upload endpoint. Default: apiBaseUrl + /agent/session/<id>/upload */
  uploadEndpoint?: (sessionId: string) => string
  /** Optional callback when user clicks "add artifact" (e.g. pin to dashboard). */
  onPinToDashboard?: (sessionId: string) => void
  /** Optional callback to open the full artifact viewer. */
  onOpenArtifactViewer?: (artifact: ArtifactInfo | null) => void
  /** Optional callback for publish/share on webapp artifacts (e.g. publish as dashboard). */
  onPublishWebapp?: (sessionId: string, artifact: ArtifactInfo) => void
  /**
   * Optional: edit a story board (a web app artifact) in place (#1416). The
   * host passes it only to people who may edit; omitted hides the action.
   * Keep it stable: it reaches every memoised message row.
   */
  onEditStory?: (sessionId: string, artifact: ArtifactInfo) => void
  /**
   * Optional: a presentation's Ask was pressed in a web app card. The host
   * opens its dialog; nothing is sent. Omitted, the page draws no Ask.
   * Keep it stable: it reaches every memoised message row.
   */
  onAskFromPage?: (sessionId: string, artifact: ArtifactInfo, ask: DeckAsk) => void
  /** Why Ask is closed here, drawn by the page beside a disabled Ask. */
  askFromPageRefusal?: string
  forkedMessageCount?: number
  /** Host composer control (chips + outgoing-message shaping). Falls back to
   *  AgentChatConfig.composer from the provider. */
  composer?: AgentComposerConfig
  /**
   * Receives a live handle on the composer (get/set the text, send) so the
   * host can drive it from outside the chat, e.g. a voice assistant typing on
   * the user's behalf. Assigned once the composer mounts, cleared on unmount.
   */
  composerHandleRef?: React.MutableRefObject<AgentComposerHandle | null>
  /**
   * Open state of the right-hand artifact panel. Omit to let AgentChat own it
   * (still closed on load) — Platinum passes it because the page holds both
   * panels' open state together.
   */
  artifactPanelOpen?: boolean
  onOpenArtifactPanel?: () => void
  onCloseArtifactPanel?: () => void
  /**
   * Host content rendered at the foot of the conversation column, after the last
   * message and before the error slot. Deliberately generic: it is a place in the
   * reading order, not a named feature. Platinum uses it for the inline
   * "starting the agent" card that replaced a blocking modal (#1294).
   */
  belowMessages?: React.ReactNode
  /**
   * A host-supplied notice drawn as the first row of the conversation column,
   * before any message — for a refusal the session opened into (AI access
   * levels and budgets, #1537), never a synthetic agent event. Distinct from
   * {@link AgentChatProps.belowMessages}: this reads first, not last, because a
   * refusal is the reason there is nothing to scroll to yet, not a footnote
   * after messages that were never sent.
   */
  notice?: ChatNoticeProps
  /**
   * Disables the composer without hiding it, for the moments when the session
   * cannot take a message yet. `readOnly` removes the composer entirely; this
   * keeps it on screen so the page does not reflow when it comes back.
   */
  composerDisabled?: boolean
  /** Placeholder while the composer is disabled or otherwise host-controlled. */
  composerPlaceholder?: string
  /**
   * The composer card's leading control — the slot before the model picker.
   * Platinum puts its Session options gear here, the same control the start
   * screen carries, so the two composers read as one (#1347 item 5).
   */
  composerLeading?: React.ReactNode
  /**
   * One-shot entrance for the conversation column, played on the first render
   * and never again. `"rise"` starts the column a screen below its resting place
   * and eases it up, for hosts whose previous screen already drew the composer
   * at the bottom edge — the composer does not move, only the conversation
   * arrives. Omit it and the column is simply there, as it has always been.
   *
   * With {@link AgentChatProps.sheet} set, the sheet is what rises — messages and
   * composer together, as one panel. Without it, the message column rises alone.
   *
   * Deliberately a name, not a description of a caller: this component has no
   * idea which screen the reader came from, and must not acquire one.
   */
  enterAnimation?: 'rise'
  /**
   * Draw the conversation as a **sheet**: one centred column, at most
   * `maxWidth` wide, carrying the messages and the composer together, with
   * rounded top corners and a soft top shadow, running to the bottom edge. The
   * page's own ground shows around it.
   *
   * For hosts whose previous screen already drew a column of the same width —
   * the sheet then reads as that column rising into place rather than the page
   * changing shape. Omit it and the layout is exactly as it has always been:
   * full width, square, no panel.
   *
   * Falls back to `AgentChatConfig.sheet` so a host can set it once.
   */
  sheet?: AgentChatSheetConfig
  /**
   * A host's own section of the right-hand panel, drawn first, above the
   * agents, tasks and artifacts. Deliberately generic: a place in the panel,
   * not a named feature. Guided conversations put the agenda tracker and the
   * notes here. With it, the closed panel's rail carries a pill for it.
   */
  panelSection?: React.ReactNode
  /** The section's name: its rail pill's tooltip and accessible name. */
  panelSectionLabel?: string
  /** What the section's rail pill shows (a few dots); absent, a list glyph. */
  panelSectionRail?: React.ReactNode
  /** Why the mic is disabled, shown on it (DisabledReason). Absent: the mic works. */
  dictationDisabledReason?: string
  /**
   * A question card's pick goes into the composer instead of being sent, and
   * a line beside the mic asks the person to say more; their next send
   * answers the card (guided conversations, Adam 29 Oct: a click sent too
   * soon, and people should talk). Absent: a click sends at once.
   */
  askUserAnswersInComposer?: boolean
}

/** How long {@link AgentChatProps.enterAnimation} `"rise"` takes. */
export const ENTER_RISE_MS = 900

export interface ChatNoticeProps {
  severity: 'error' | 'info'
  text: string
  /** e.g. a "Request access" button. Rendered after the text. */
  action?: React.ReactNode
}

/**
 * The first row of the conversation column: a host-level notice, not a
 * message. Used for an AI access or budget refusal the session opened into
 * (#1537) — a red-bordered card the reader sees before anything else, rather
 * than an error the composer reveals only once they try to type.
 *
 * A named export because hosts draw the same card above their own composer
 * on the start screen, before a session exists to pass `notice` to.
 */
export function ChatNotice({ severity, text, action }: ChatNoticeProps) {
  return (
    <Box
      data-testid="agent-chat-notice"
      sx={{
        ...CARD_SX,
        border: '1px solid',
        borderColor: severity === 'error' ? 'error.main' : HAIRLINE_COLOR,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        mb: 2,
      }}
    >
      <Typography sx={{ flex: 1, fontSize: 13, color: severity === 'error' ? 'error.main' : 'text.secondary' }}>
        {text}
      </Typography>
      {action}
    </Box>
  )
}


// Stable stand-ins for the "no prop, no context" case. A fresh `[]`, `new Map()`
// or `() => {}` per render would change identity every time AgentChat renders,
// which flows into the useMemos below and from there into every MessageRow's
// props — defeating the row memo for the whole conversation.
const EMPTY_ARRAY: never[] = []
const EMPTY_MAP = new Map<string, never>()
const NOOP = () => {}

/**
 * The chat. Its workflow agents are read through a `WorkflowAgentsSource`
 * from context (the design showcase supplies a fixture one); with none, one
 * is built here from the chat's endpoints and auth header, so the Agents
 * list, the rail and the drawer all share one poll per run.
 */
export default function AgentChat(props: AgentChatProps) {
  const ctx = useAgentChatContextOptional()
  const outer = React.useContext(WorkflowAgentsSourceContext)
  // The endpoints come from the provider, so they take the provider's base:
  // the chat page's endpoints already start with /api/v1 under a '' base,
  // while its apiBaseUrl prop is '/api/v1' (for uploads and dictation).
  const apiBaseUrl = ctx ? ctx.config.apiBaseUrl ?? '' : props.apiBaseUrl ?? ''
  const endpoints = ctx?.config.endpoints
  const authHeader = props.authHeader
  const own = useMemo(
    () => (outer ? null : createWorkflowAgentsSource({ apiBaseUrl, endpoints, authHeader })),
    [outer, apiBaseUrl, endpoints, authHeader],
  )
  if (!own) return <AgentChatBody {...props} />
  return (
    <WorkflowAgentsSourceContext.Provider value={own}>
      <AgentChatBody {...props} />
    </WorkflowAgentsSourceContext.Provider>
  )
}

function AgentChatBody(props: AgentChatProps) {
  // Read optional context — null when used outside a Provider (legacy prop-only usage).
  const ctx = useAgentChatContextOptional()

  // Resolve each value: explicit prop takes precedence, then context, then empty default.
  const messages        = props.messages        ?? ctx?.messages        ?? EMPTY_ARRAY
  const queuedMessages  = props.queuedMessages  ?? ctx?.queuedMessages  ?? EMPTY_ARRAY
  const isStreaming     = props.isStreaming      ?? ctx?.isStreaming     ?? false
  const error           = props.error           ?? ctx?.error           ?? null
  const activityStatus  = props.activityStatus  ?? ctx?.activityStatus  ?? null
  const onSendMessage   = props.onSendMessage   ?? ctx?.sendMessage     ?? NOOP
  const onCancel        = props.onCancel        ?? ctx?.cancelSession   ?? NOOP
  const artifacts       = props.artifacts       ?? ctx?.artifacts       ?? EMPTY_ARRAY
  const todos           = props.todos           ?? ctx?.todos           ?? EMPTY_ARRAY
  const renderedTables  = props.renderedTables  ?? ctx?.renderedTables  ?? (EMPTY_MAP as ReadonlyMap<string, RenderedTableInfo>)
  const renderedCharts  = props.renderedCharts  ?? ctx?.renderedCharts  ?? (EMPTY_MAP as ReadonlyMap<string, RenderedChartInfo>)
  const askedQuestions  = props.askedQuestions  ?? ctx?.askedQuestions  ?? (EMPTY_MAP as ReadonlyMap<string, AskUserQuestionInfo>)
  const createdDashboards = props.createdDashboards ?? ctx?.createdDashboards ?? (EMPTY_MAP as ReadonlyMap<string, CreatedDashboardInfo>)
  const onAnswerQuestion = props.onAnswerQuestion ?? ctx?.markQuestionAnswered ?? NOOP
  const sessionId       = props.sessionId       ?? ctx?.session?.id    ?? ''
  const toolInputBuffer = props.toolInputBuffer ?? ctx?.toolInputBuffer ?? ''
  const selectedModel   = props.selectedModel   ?? ctx?.selectedModel  ?? ''
  const onModelChange   = props.onModelChange   ?? ctx?.setSelectedModel ?? NOOP
  const models          = props.models          ?? ctx?.config.models   ?? EMPTY_ARRAY
  const defaultModel    = ctx?.config.defaultModel ?? ''

  // A session created before a model was retired from the list still carries
  // that id. Resolve it to a real entry once, here, so the dropdown and the
  // message payload can never disagree about which model is in use.
  //
  // Falls back to the configured default, NOT models[0]: the list is ordered
  // most-capable-first, so its first entry is the most expensive one, and a
  // stale selection would silently upgrade the user's cost.
  const modelIsOffered = models.some(m => m.id === selectedModel)
  const fallbackModel = models.some(m => m.id === defaultModel) ? defaultModel : models[0]?.id ?? ''
  const effectiveModel = models.length > 0 && !modelIsOffered ? fallbackModel : selectedModel

  const _lastHeartbeat  = props.lastHeartbeat   ?? ctx?.lastHeartbeat
  const stuckStatus     = props.stuckStatus     ?? ctx?.stuckStatus
  const onNudge         = props.onNudge         ?? ctx?.nudgeAgent
  const readOnly        = props.readOnly
  const plugins         = props.plugins         ?? ctx?.config.plugins ?? EMPTY_ARRAY
  const pluginEvents    = props.pluginEvents    ?? ctx?.pluginEvents   ?? EMPTY_ARRAY
  const apiBaseUrl      = props.apiBaseUrl      ?? ctx?.config.apiBaseUrl ?? ''
  const authHeader      = props.authHeader
  const transcribeEndpoint = props.transcribeEndpoint
  const uploadEndpoint  = props.uploadEndpoint
  const onPinToDashboard = props.onPinToDashboard
  const onOpenArtifactViewer = props.onOpenArtifactViewer
  const onPublishWebapp = props.onPublishWebapp
  const onEditStory = props.onEditStory
  const onAskFromPage = props.onAskFromPage
  const askFromPageRefusal = props.askFromPageRefusal
  const forkedMessageCount = props.forkedMessageCount
  const composer        = props.composer        ?? ctx?.config.composer
  // #1296: host hook for inline citation markers. Passthrough only.
  const renderInlineCitations = ctx?.config.renderInlineCitations
  const belowMessages   = props.belowMessages
  const notice          = props.notice
  const enterAnimation  = props.enterAnimation
  const sheet           = props.sheet           ?? ctx?.config.sheet
  const composerDisabled = props.composerDisabled ?? false
  const composerPlaceholder = props.composerPlaceholder ?? 'Type a message...'
  const composerLeading = props.composerLeading
  const panelSection = props.panelSection
  const panelSectionLabel = props.panelSectionLabel ?? 'Panel'
  const panelSectionRail = props.panelSectionRail
  const dictationDisabledReason = props.dictationDisabledReason
  const askUserAnswersInComposer = props.askUserAnswersInComposer === true

  // Unsent text is kept per chat in sessionStorage (#1836); a restored draft
  // is non-empty, so the suggestion guard below never overwrites it.
  const [input, setInput] = useComposerDraft(sessionId || undefined)
  // Set by a card pick; the line beside the mic shows while the pick is
  // still in the box and goes once it is sent or cleared.
  const [picked, setPicked] = useState(false)
  const micNudge = picked && input.trim() !== ''
    ? (dictationDisabledReason
        ? 'Add anything in your own words, then press Send.'
        : 'Say more in your own words: use the microphone, or type. Then press Send.')
    : undefined

  // A host's suggested opening line, adopted ONLY into an empty box and only
  // once per distinct value. What the user has typed is theirs — a suggestion
  // that overwrote it, or appended to it, would be worse than no suggestion.
  //
  // Render-phase guard rather than an effect (see CLAUDE.md): this is one-shot
  // initialisation with nothing to clean up, and an effect would paint the
  // empty box first and then replace it.
  const suggestedText = composer?.suggestedText ?? ''
  const lastSuggestionRef = useRef('')
  if (suggestedText !== lastSuggestionRef.current) {
    const previous = lastSuggestionRef.current
    lastSuggestionRef.current = suggestedText
    // Empty box, or a box still holding the previous suggestion word for word
    // and therefore untouched. Attaching a second thing may refine a
    // suggestion nobody has edited; it may never edit what the user wrote.
    // Clearing it re-arms, so emptying the chips and starting again offers a
    // fresh suggestion.
    if (suggestedText && (input === '' || input === previous)) setInput(suggestedText)
  }

  // The right panel is controlled when the host passes artifactPanelOpen, and
  // self-managed otherwise. Either way it starts closed: everything on this page
  // starts minimised.
  const [ownArtifactPanelOpen, setOwnArtifactPanelOpen] = useState(false)
  const artifactPanelOpen = props.artifactPanelOpen ?? ownArtifactPanelOpen
  const openArtifactPanel = props.onOpenArtifactPanel ?? (() => setOwnArtifactPanelOpen(true))
  const closeArtifactPanel = props.onCloseArtifactPanel ?? (() => setOwnArtifactPanelOpen(false))

  // Chief of Staff's delegated agents (plan
  // design/2026-10-26-chief-of-staff-agents-panel.md, T7): derived from the
  // messages, never stored, and listed in the right panel and on its rail.
  // Every workflow with a run id is polled here, once, and the runs are
  // shared through DelegatedAgentsContext with the panel, the rail, the
  // cards and the drawer. Nothing here opens the panel: only a workflow
  // card's click does (showWorkflow), as the reader asked for it.
  const delegatedAgents = useMemo(() => deriveDelegatedAgents(messages), [messages])
  const workflowRunRequests = delegatedAgents.flatMap(e =>
    e.kind === 'workflow' && e.info.runId ? [{ runId: e.info.runId, running: e.info.status === 'running' }] : [])
  const { runs: workflowRuns, errors: workflowRunErrors, retry: retryWorkflowRun } = useWorkflowRuns(sessionId, workflowRunRequests)
  const [openAgentRef, setOpenAgentRef] = useState<AgentRef | null>(null)
  // Read by the double-Escape handler, which must not re-subscribe on it.
  const openAgentRefRef = useRef(openAgentRef)
  openAgentRefRef.current = openAgentRef
  const [focusWorkflow, setFocusWorkflow] = useState<{ toolCallId: string; nonce: number } | null>(null)
  // Another chat in the same AgentChat (History switches without a remount):
  // close the previous chat's drawer and forget its focus, in render.
  const delegatedSessionRef = useRef(sessionId)
  if (delegatedSessionRef.current !== sessionId) {
    delegatedSessionRef.current = sessionId
    if (openAgentRef) setOpenAgentRef(null)
    if (focusWorkflow) setFocusWorkflow(null)
  }
  // Read through a ref so the API keeps its identity across renders: a host
  // may hand a fresh onOpenArtifactPanel each time.
  const openArtifactPanelRef = useRef(openArtifactPanel)
  openArtifactPanelRef.current = openArtifactPanel
  const delegatedApi = useMemo<DelegatedAgentsApi>(() => ({
    // The drawer opens beside the right panel, which opens with it, so the
    // other agents stay one click away.
    openAgent: (ref) => {
      openArtifactPanelRef.current()
      setOpenAgentRef(ref)
    },
    showWorkflow: (toolCallId) => {
      openArtifactPanelRef.current()
      setFocusWorkflow(f => ({ toolCallId, nonce: (f?.nonce ?? 0) + 1 }))
    },
    runs: workflowRuns,
    selected: openAgentRef,
    runErrors: workflowRunErrors,
    retryRun: retryWorkflowRun,
  }), [workflowRuns, workflowRunErrors, retryWorkflowRun, openAgentRef])
  // One-shot badge pulse when the session's first artifact lands behind a closed
  // panel. Deliberately not an auto-open: nothing moves under the reader.
  const [artifactPulse, setArtifactPulse] = useState(false)
  const prevArtifactCountRef = useRef(artifacts.length)
  const [viewerArtifact, setViewerArtifact] = useState<ArtifactInfo | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const columnRef = useRef<HTMLDivElement>(null)

  // The scroll rule (Matt, 11 Sep, #1347): follow the bottom only while the
  // reader is AT the bottom, and stop the moment they scroll up. Anything
  // that arrives below them then raises a "Jump to latest" pill instead of
  // pulling them off what they are reading. `stickRef` is the reader's
  // position as of their last scroll; the ResizeObserver on the column is
  // what notices content arriving — a streamed word, a rendered table, an
  // image finishing — which a message count never could.
  const stickRef = useRef(true)
  const contentHeightRef = useRef(0)
  // Two separate facts. `awayFromBottom` is where the reader is, and it alone
  // decides whether the jump button is on screen — a button that takes you to
  // the foot is only ever useful when you are not already there. `newBelow`
  // is whether anything has ARRIVED since they left the foot, and it only
  // changes what the button's tooltip says.
  const [awayFromBottom, setAwayFromBottom] = useState(false)
  const [newBelow, setNewBelow] = useState(false)
  const scrollToBottom = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el) return
    stickRef.current = true
    el.scrollTop = el.scrollHeight
    setAwayFromBottom(false)
    setNewBelow(false)
  }, [])
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el) return
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24
    stickRef.current = atBottom
    setAwayFromBottom(!atBottom)
    if (atBottom) setNewBelow(false)
  }, [])
  useEffect(() => {
    const col = columnRef.current
    if (!col || typeof ResizeObserver === 'undefined') return
    // The observer reports the current size as soon as it is attached; that
    // first report is the starting height, not an arrival.
    contentHeightRef.current = col.offsetHeight
    // A column with no height is hidden (display: none: the story board
    // editor hides the conversation while it is open). Coming back from that
    // is the same content drawn again, not an arrival, so it raises no
    // "new below" signal.
    let hidden = false
    const observer = new ResizeObserver(() => {
      const el = scrollContainerRef.current
      if (!el) return
      const height = col.offsetHeight
      if (height === 0) {
        hidden = true
        contentHeightRef.current = 0
        return
      }
      const reshown = hidden
      hidden = false
      const grew = !reshown && height > contentHeightRef.current
      contentHeightRef.current = height
      if (stickRef.current) {
        el.scrollTop = el.scrollHeight
      } else if (grew) {
        setNewBelow(true)
      }
    })
    observer.observe(col)
    return () => observer.disconnect()
  }, [])
  // The composer bed's height, measured, when it is laid over the scroll box:
  // the box pads its foot by this much so nothing is left hidden behind the
  // card. Chips, a growing textarea and status lines all change it.
  const footBedRef = useRef<HTMLDivElement>(null)
  const footRef = useRef<HTMLDivElement>(null)
  const [footHeight, setFootHeight] = useState(0)
  // Where the composer card's right edge and top line sit against the frame,
  // so the jump button can take its place beside the living circle, which is
  // centred on the card's Send button (Adam, 13 Sep).
  const [footEdge, setFootEdge] = useState({ right: 0, top: 0 })
  const overlayFoot = Boolean(sheet && !sheet.fullWidth)
  useEffect(() => {
    const el = footBedRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const measure = () => {
      setFootHeight(el.offsetHeight)
      const frame = el.parentElement
      const foot = footRef.current
      if (frame && foot) {
        const f = frame.getBoundingClientRect()
        const c = foot.getBoundingClientRect()
        setFootEdge(prev => (prev.right === f.right - c.right && prev.top === f.bottom - c.top ? prev : { right: f.right - c.right, top: f.bottom - c.top }))
      }
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    if (footRef.current) observer.observe(footRef.current)
    return () => observer.disconnect()
  }, [overlayFoot])
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const prevMsgLenRef = useRef(messages.length)
  const prevStreamingRef = useRef(isStreaming)
  /** Timestamp of the last unpaired Escape; 0 when there is none. */
  const lastEscapeAtRef = useRef(0)
  // Latest send/answer handlers, read through stable wrappers below so a host
  // that hands us a fresh lambda each render cannot break the row memo.
  const onAnswerQuestionRef = useRef<(toolCallId: string, value: string) => void>(NOOP)
  const onSendMessageRef = useRef<(content: string, model?: string, attachmentIds?: string[], effort?: string) => void>(NOOP)

  useEffect(() => {
    const had = prevArtifactCountRef.current
    prevArtifactCountRef.current = artifacts.length
    if (had > 0 || artifacts.length === 0 || artifactPanelOpen) return
    setArtifactPulse(true)
  }, [artifacts.length])
  // The pulse lets go one beat after it starts, on its own clock. It used to
  // share the effect above, whose cleanup cancelled the reset whenever a
  // second artifact landed inside the beat (a restored session, a tool that
  // writes several files at once), leaving the edge strip out for good
  // (Adam, 14 Sep).
  useEffect(() => {
    if (!artifactPulse) return
    const t = setTimeout(() => setArtifactPulse(false), EDGE_PULSE_MS)
    return () => clearTimeout(t)
  }, [artifactPulse])

  // Escape pressed twice inside DOUBLE_ESCAPE_WINDOW_MS stops the turn, the
  // same habit the Claude Code terminal builds. A single Escape does nothing.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !isStreaming || readOnly) return
      // An Escape that closed the agent drawer (it marks the event) is not
      // half of a stop: a Chief of Staff turn stops every agent with it.
      if (e.defaultPrevented || openAgentRefRef.current) return
      const now = Date.now()
      const previous = lastEscapeAtRef.current
      if (previous > 0 && now - previous <= DOUBLE_ESCAPE_WINDOW_MS) {
        lastEscapeAtRef.current = 0
        e.preventDefault()
        onCancel()
        return
      }
      lastEscapeAtRef.current = now
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isStreaming, readOnly, onCancel])

  // #1236: the default upload endpoint resolves under apiBaseUrl, exactly as
  // the transcription endpoint does below — no caller can post to the SPA.
  const fileAttachments = useFileAttachments({
    uploadEndpoint,
    apiBaseUrl,
    authHeader,
  })

  const handleTranscription = useCallback((text: string) => {
    setInput(prev => prev ? prev + ' ' + text : text)
  }, [])

  const resolvedTranscribeEndpoint = transcribeEndpoint || `${apiBaseUrl}/transcribe`

  const voiceDictation = useVoiceDictation({
    onTranscription: handleTranscription,
    transcribeEndpoint: resolvedTranscribeEndpoint,
    authHeader,
  })

  const handleToggleRecording = useCallback(() => {
    if (voiceDictation.isRecording) {
      voiceDictation.stopRecording()
    } else {
      voiceDictation.startRecording()
    }
  }, [voiceDictation.isRecording, voiceDictation.stopRecording, voiceDictation.startRecording])

  // Sending while a turn runs is allowed: the session QUEUES the message
  // rather than let it supersede the running turn (useAgentSession.sendMessage).
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim() || composerDisabled) return
    let attachmentIds: string[] | undefined
    if (fileAttachments.attachments.length > 0) {
      attachmentIds = await fileAttachments.uploadAll(sessionId)
      fileAttachments.clear()
    }
    // The host may reshape the outgoing text (e.g. append a trailing block
    // naming the chipped tables). With no composer config the typed text is
    // sent verbatim, exactly as before.
    const content = composer ? composer.buildMessage(input.trim()) : input.trim()
    // effectiveModel, not selectedModel: a session created before a model was
    // retired still carries the old id, and the payload must name the same
    // model the picker is showing.
    onSendMessage(content, effectiveModel, attachmentIds)
    // The send is the answer to any open card, as a replay reads it
    // (replayEvents markAnsweredQuestions).
    if (askUserAnswersInComposer) {
      for (const [id, q] of askedQuestions) if (!q.answered) onAnswerQuestion(id, content)
      setPicked(false)
    }
    setInput('')
    // Sending is the reader's own move to the foot of the conversation.
    scrollToBottom()
    // Clears the host's chips alongside the input. onSendMessage streams the
    // reply asynchronously and swallows its own errors, so there is no later
    // moment to hang this on.
    composer?.onSent?.()
  // effectiveModel, not selectedModel: the body sends effectiveModel (see the
  // note above it), so listing selectedModel here would leave the callback
  // holding a stale resolution. composerDisabled comes from main's new guard.
  }, [input, composerDisabled, onSendMessage, effectiveModel, fileAttachments.attachments.length, fileAttachments.uploadAll, fileAttachments.clear, sessionId, composer, scrollToBottom, askUserAnswersInComposer, askedQuestions, onAnswerQuestion])

  // Live handle for hosts driving the composer from outside (voice assistant).
  // Refs, not deps: the handle must always act on the latest input and
  // submit path without re-assigning on every keystroke.
  const inputRef = useRef(input)
  inputRef.current = input
  const handleSubmitRef = useRef(handleSubmit)
  handleSubmitRef.current = handleSubmit
  // effectiveModel, not selectedModel: the handle reports the model the next
  // message will actually carry, which is what the picker shows.
  const effectiveModelRef = useRef(effectiveModel)
  effectiveModelRef.current = effectiveModel
  const modelsRef = useRef(models)
  modelsRef.current = models
  const composerHandleRef = props.composerHandleRef
  useEffect(() => {
    if (!composerHandleRef) return
    composerHandleRef.current = {
      getInput: () => inputRef.current,
      setInput: (text: string) => setInput(text),
      send: () => handleSubmitRef.current({ preventDefault() {} } as unknown as React.FormEvent),
      getModel: () => effectiveModelRef.current,
      setModel: (id: string) => { if (modelsRef.current.some((mm) => mm.id === id)) onModelChange(id) },
      models: () => modelsRef.current.map((mm) => ({ id: mm.id, label: mm.label })),
    }
    return () => { composerHandleRef.current = null }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composerHandleRef])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e as unknown as React.FormEvent)
    }
  }, [handleSubmit])

  // Queued messages sit below everything the running turn writes.
  const displayMessages = useMemo(
    () => (queuedMessages.length > 0 ? [...messages, ...queuedMessages] : messages),
    [messages, queuedMessages],
  )

  if (messages.length !== prevMsgLenRef.current) {
    prevMsgLenRef.current = messages.length
  }

  if (prevStreamingRef.current && !isStreaming) {
    textareaRef.current?.focus()
  }
  prevStreamingRef.current = isStreaming

  // Each row's own slice of the session's maps: the plugin fold, the artifact
  // lists and the tool-call maps, kept stable per row (useRowSlices, rowProps.ts).
  const rowSlices = useRowSlices({
    messages,
    displayMessages,
    artifacts,
    renderedTables,
    renderedCharts,
    askedQuestions,
    createdDashboards,
    plugins,
    pluginEvents,
    isStreaming,
  })

  // The picker is only meaningful when there is more than one model to pick.
  const hasModelPicker = models.length >= 2

  const handleOpenPreview = useCallback((a: ArtifactInfo) => {
    setViewerArtifact(a)
    if (onOpenArtifactViewer) onOpenArtifactViewer(a)
  }, [onOpenArtifactViewer])

  // Rows read these through props, so they have to keep their identity across a
  // render that changed only the container (the width toggle, a panel sliding).
  const handleAnswerQuestion = useCallback((toolCallId: string, value: string) => {
    onAnswerQuestionRef.current(toolCallId, value)
  }, [])
  const askedQuestionsRef = useRef(askedQuestions)
  askedQuestionsRef.current = askedQuestions
  const handlePickAnswer = useCallback((toolCallId: string, value: string) => {
    const options = askedQuestionsRef.current.get(toolCallId)?.options ?? []
    setInput(prev => fillAnswerDraft(prev, value, options))
    setPicked(true)
    textareaRef.current?.focus()
  }, [])
  const handleSendMessage = useCallback((content: string, model?: string, attachmentIds?: string[]) => {
    onSendMessageRef.current(content, model, attachmentIds)
  }, [])
  onAnswerQuestionRef.current = onAnswerQuestion
  onSendMessageRef.current = onSendMessage

  const rising = enterAnimation === 'rise'
  // The rise belongs to the message list: it comes up from the bottom of the
  // screen behind a composer that was already sitting there.
  const riseSx = {
    animation: `agentChatEnterRise ${ENTER_RISE_MS}ms cubic-bezier(0.2, 0, 0, 1)`,
    '@keyframes agentChatEnterRise': {
      from: { transform: 'translateY(100%)' },
      to:   { transform: 'translateY(0)' },
    },
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  }

  // The reading column. This constraint deliberately does NOT live on the box
  // that scrolls: a browser draws the scrollbar at the edge of the scrolling
  // box, so capping the width there put the bar a couple of hundred pixels in
  // from the edge, running down the middle of the page. Scroll on the outside,
  // width on the inside, and the bar lands on the edge of the chat area —
  // which is left of the artifacts panel when that is open and at the window
  // edge when it is closed, with no conditional, because the scroll box is
  // already the artifacts panel's flex sibling.
  const columnSx = {
    width: '100%',
    ...(sheet ? {
      maxWidth: sheet.fullWidth ? 'none' : sheet.maxWidth,
      // The auto margins take the slack the cap leaves, so the column centres.
      mx: 'auto',
      boxSizing: 'border-box' as const,
      // At full width there is no slack left to breathe with, so the text would
      // otherwise run into the scrollbar on one side and the panel on the other.
      // In the column, the gutter is the card's own padding: the column's edge
      // (the bar down an assistant message, a user bubble's right edge) then
      // sits inside the card's rounded corners rather than flush with them.
      px: sheet.fullWidth ? '40px' : 2,
    } : {}),
  }

  // The foot (status lines and composer) keeps the column's width so it does
  // not go full width under narrow messages — but NOT its full-width gutter:
  // at full width the composer is a flat bar that runs edge to edge (it steps
  // its own controls in past the edge strips), and a 40px inset would leave a
  // strip of page ground either side of it.
  const footSx = {
    width: '100%',
    ...(sheet ? {
      maxWidth: sheet.fullWidth ? 'none' : sheet.maxWidth,
      mx: 'auto',
    } : {}),
  }

  // The collapsed artifacts rail (EdgeReveal, right edge) is an overlay on the
  // chat area. The message scroll box steps in by its width so the scrollbar
  // is not hidden under it; the composer's bed steps in by the same width, or
  // the column centred in the scroll box and the card centred in the full
  // width sit half a rail apart (Adam, 10 Sep). No rail when the panel is
  // open (EdgeReveal unmounts) or in read-only mode, so no step then. At full
  // width the bar deliberately runs under the rail, edge to edge.
  const railReserved = !readOnly && !artifactPanelOpen
  // The turn's activity, slowed to a readable pace, for the living circle
  // above the composer (Adam, 13 Sep). Active while a turn streams, and for a
  // system status (restoring, connecting) before one has started.
  const statusLine = composerStatusLine(
    isStreaming && activityStatus?.label ? activityStatus.label : undefined,
    delegatedAgents,
    workflowRuns,
  )
  const activity = useActivityLog(activityStatus, !readOnly && Boolean(activityStatus) && (isStreaming || activityStatus?.category === 'system'))

  // What the jump button says on hover. With something waiting below, the
  // newest message itself — one line of it, so the reader can decide whether
  // it is worth leaving what they are reading for. A message that is still
  // only tool calls or thinking has no text to show, and the plain label
  // stands in rather than an empty bubble.
  const newestBelow = newBelow ? displayMessages[displayMessages.length - 1] : undefined
  const newestBelowText = newestBelow?.content?.replace(/\s+/g, ' ').trim() ?? ''
  const jumpTooltip = newestBelowText
    ? `${newestBelow?.role === 'user' ? 'You' : 'Agent'}: ${newestBelowText.length > 90 ? `${newestBelowText.slice(0, 90)}…` : newestBelowText}`
    : 'Jump to latest'
  // In the column sheet the card is laid OVER the foot of the scroll box, so
  // the list scrolls behind it (Adam, 10 Sep); the box keeps a bottom gutter
  // the card's height so the last message can still be scrolled clear. The
  // full-width bar stays in flow: laying a bar across the whole area would
  // hide the scrollbar's foot.
  const footBedSx = {
    pr: railReserved && sheet && !sheet.fullWidth ? `${EDGE_STRIP_WIDTH}px` : 0,
    ...(overlayFoot ? {
      position: 'absolute' as const,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1,
      // The bed spans the box; only the card in it is solid, so the ground
      // beside the card and the scrollbar's foot still take the pointer.
      pointerEvents: 'none' as const,
    } : {}),
  }

  // Only the message box scrolls, but the reader's wheel lands wherever the
  // pointer is — over the composer as often as not. A wheel anywhere in the
  // chat area that is not already inside the scroll box moves the messages.
  // Inside it the browser scrolls natively and this must stay out of the way.
  const handleWheel = (e: React.WheelEvent) => {
    const el = scrollContainerRef.current
    if (!el || el.contains(e.target as Node)) return
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientHeight : 1
    el.scrollTop += e.deltaY * unit
  }

  return (
    <DelegatedAgentsContext.Provider value={delegatedApi}>
    <Box sx={{ display: 'flex', flex: 1, minHeight: 0, position: 'relative' }}>
      {/* Chat area. With `sheet` this is one centred column: the message list
          above, the composer as a bordered, rounded card at the foot, the
          page's ground around both. */}
      <Box
        data-testid={sheet ? 'agent-chat-sheet' : undefined}
        onWheel={handleWheel}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          // Full width, always: the cap now sits on the column inside, so this
          // box's right edge is where the scrollbar belongs.
          ...(sheet ? { width: '100%' } : {}),
          // The frame the overlaid composer bed and the jump button position
          // against. Always relative: the button is there at full width too.
          position: 'relative',
        }}
      >
        {/* The glimpse of the closed artifacts panel: what the session has
            made and how far its plan has got, running down the right edge
            from the top, where the panel would open from (Adam, 12 Sep).
            Gone the moment the panel is open — the panel then says all of
            it. Sits just inside the edge rail. */}
        {!readOnly && !artifactPanelOpen && (
          <Box sx={{ position: 'absolute', top: 12, right: railReserved ? EDGE_STRIP_WIDTH + 16 + COMPOSER_BUTTON_SX.minWidth / 2 - PREVIEW_RAIL_WIDTH / 2 : 8, zIndex: 2 }}>
            <ArtifactPanelPreview
              artifacts={artifacts}
              todos={todos}
              agents={delegatedAgents}
              section={panelSection ? { label: panelSectionLabel, rail: panelSectionRail } : undefined}
              onOpen={openArtifactPanel}
            />
          </Box>
        )}
        {/* Messages */}
        <Box
          ref={scrollContainerRef}
          onScroll={handleScroll}
          data-testid="agent-chat-messages"
          data-enter-animation={rising ? 'rise' : undefined}
          sx={{
            flex: 1,
            overflow: 'auto',
            pt: 2,
            px: 2,
            // The card's height plus the usual gutter, so the end of the
            // conversation can scroll out from behind the card.
            // Plus the controls that stand above the card's top edge, so the
            // last line of a reply never sits under them.
            pb: `${(overlayFoot ? footHeight + 16 : 16) + COMPOSER_OVERHANG_PX}px`,
            position: 'relative',
            // The collapsed artifacts rail (EdgeReveal, right edge) is an overlay
            // sitting right on top of this box's native scrollbar — a closed
            // panel otherwise leaves the scrollbar invisible under the rail.
            // Reserve the rail's width so the scroll box (and its scrollbar)
            // ends just before it starts. No rail exists when the panel is
            // open (EdgeReveal unmounts) or in read-only mode, so no gap then.
            mr: railReserved ? `${EDGE_STRIP_WIDTH}px` : 0,
            ...(rising ? riseSx : {}),
          }}
        >
          <Box ref={columnRef} data-testid="agent-chat-column" sx={columnSx}>
            {notice && <ChatNotice {...notice} />}
            {displayMessages.map((message, index) => (
              <MessageRow
                key={message.id}
                message={message}
                activelyThinking={
                  isStreaming
                  && activityStatus?.category === 'thinking'
                  // The last CONVERSATION row: queued messages below it are not the turn.
                  && index === messages.length - 1
                }
                showForkDivider={forkedMessageCount != null && forkedMessageCount > 0 && index === forkedMessageCount - 1}
                autoArtifacts={rowSlices[index].autoArtifacts}
                toolArtifacts={rowSlices[index].toolArtifacts}
                sessionId={sessionId}
                apiBaseUrl={apiBaseUrl}
                authHeader={authHeader}
                isStreaming={rowSlices[index].isStreaming}
                onOpenPreview={handleOpenPreview}
                onPublishWebapp={onPublishWebapp}
                onEditStory={onEditStory}
                onAskFromPage={onAskFromPage}
                askFromPageRefusal={askFromPageRefusal}
                onAnswerQuestion={handleAnswerQuestion}
                onSendMessage={handleSendMessage}
                onPickAnswer={askUserAnswersInComposer ? handlePickAnswer : undefined}
                renderInlineCitations={renderInlineCitations}
                plugins={plugins}
                byPlugin={rowSlices[index].byPlugin}
                toolCallTables={rowSlices[index].toolCallTables}
                toolCallCharts={rowSlices[index].toolCallCharts}
                toolCallQuestions={rowSlices[index].toolCallQuestions}
                toolCallDashboards={rowSlices[index].toolCallDashboards}
              />
            ))}

            {/* Host slot at the foot of the conversation: reads as the next thing
                after the last message, which is where a wait belongs. */}
            {belowMessages}

            {/* A host that drew this error as the notice (a refused turn) has
                already said it at the top; do not say it twice. */}
            {error && error !== notice?.text && (
              <Alert severity="error" sx={{ mt: 1, fontSize: 13 }}>
                {error}
              </Alert>
            )}

          </Box>
        </Box>

        {/* Jump to the foot of the conversation. A FAB in the bottom-right
            corner of the chat area (Adam, 11 Sep), clear of the composer card
            and of the artifacts rail, shown whenever the reader is not at the
            bottom. Its tooltip is where the news goes: with something waiting
            below it names the newest message, otherwise it just says what the
            button does. */}
        <Fade in={awayFromBottom} timeout={{ enter: 260, exit: 200 }} mountOnEnter unmountOnExit>
          <Box
            sx={{
                position: 'absolute',
                zIndex: 2,
                // Beside the living circle while a turn runs: the same centre
                // line as the card's top edge, to the circle's left (Adam,
                // 13 Sep). Otherwise clear of the card, bottom right.
                ...(!readOnly
                  ? { bottom: Math.max(16, footEdge.top - 20), right: footEdge.right + composerButtonCentreInset(sheet?.fullWidth ? 'bar' : 'docked') + ACTIVITY_BADGE_SIZE / 2 + 8 }
                  // At rest: clear of the card by the usual gutter, whichever
                  // shape the card is. footEdge.top is the card's top line above
                  // the frame's bottom; before it is measured, the bed's height.
                  : { bottom: (footEdge.top > 0 ? footEdge.top : overlayFoot ? footHeight : 0) + 16, right: railReserved ? EDGE_STRIP_WIDTH + 16 : 16 }),
            }}
          >
          <Tooltip title={jumpTooltip} placement="left" arrow>
            <Fab
              data-testid="agent-jump-to-latest"
              size="small"
              aria-label={jumpTooltip}
              onClick={scrollToBottom}
              sx={{
                bgcolor: 'background.paper',
                color: 'text.primary',
                border: HAIRLINE,
                boxShadow: 'none',
                '&:hover': { bgcolor: 'background.paper', boxShadow: 'none' },
                // The dot says there is something new down there; the tooltip
                // says what. No dot when the reader has simply scrolled up.
                ...(newBelow ? {
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    backgroundColor: '#1d4ed8',
                    border: '2px solid',
                    borderColor: 'background.paper',
                  },
                } : {}),
              }}
            >
              <ArrowDownwardIcon sx={{ fontSize: 18 }} />
            </Fab>
          </Tooltip>
          </Box>
        </Fade>

        {/* Everything below the scroll region — the status lines and the
            composer — keeps the same column as the messages above it, or it
            would go full width while they stayed narrow. */}
        <Box ref={footBedRef} data-testid="agent-chat-foot-bed" sx={footBedSx}>
        <Box ref={footRef} data-testid="agent-chat-foot" sx={{ minWidth: 0, pointerEvents: 'auto', ...footSx }}>

          {/* The activity line ("Running: build_deck.py", "Thinking...") is no
              longer a strip above the card: it sits at the right-hand end of the
              composer's toolbar row, on the same line as the model picker, the
              clip and the mic (Adam, 11 Sep). See the toolbar row below. */}

          {/* Possibly stuck banner */}
          {!readOnly && isStreaming && stuckStatus === 'possibly_stuck' && (
            <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.5, backgroundColor: '#fffbeb', borderTop: '1px solid #fde68a' }}>
              <Typography sx={{ fontSize: 13, flex: 1, color: '#92400e' }}>
                The agent has been quiet for a while. It may be working on something complex.
              </Typography>
              <Button variant="outlined" color="info" size="small" onClick={onCancel} sx={{ textTransform: 'none', fontSize: 12, whiteSpace: 'nowrap' }}>
                Stop
              </Button>
            </Box>
          )}

          {/* Stuck detection banner */}
          {!readOnly && isStreaming && stuckStatus === 'likely_stuck' && (
            <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.5, backgroundColor: '#fef2f2', borderTop: '1px solid #fecaca' }}>
              <Typography sx={{ fontSize: 13, flex: 1, color: '#991b1b' }}>
                The agent appears to be stuck. No activity for over 2 minutes.
              </Typography>
              {onNudge && (
                <Button variant="contained" color="info" size="small" onClick={onNudge} sx={{ textTransform: 'none', fontSize: 12, whiteSpace: 'nowrap' }}>
                  Send Nudge
                </Button>
              )}
              <Button variant="outlined" color="info" size="small" onClick={onCancel} sx={{ textTransform: 'none', fontSize: 12, whiteSpace: 'nowrap' }}>
                Stop
              </Button>
            </Box>
          )}

          {/* Input: the one composer card (ComposerCard), shared with the
              start screen. The session supplies the status line as the
              trailing element and Send/Stop from its streaming state. */}
          {!readOnly && (
            <ComposerCard
              component="form"
              testId="agent-chat-composer"
              shape={sheet ? (sheet.fullWidth ? 'bar' : sheet.squareTop ? 'square' : 'docked') : 'plain'}
              onSubmit={handleSubmit}
              onDragOver={(e: React.DragEvent) => { e.preventDefault(); e.stopPropagation() }}
              onDrop={(e: React.DragEvent) => {
                e.preventDefault()
                e.stopPropagation()
                if (e.dataTransfer.files.length > 0) {
                  fileAttachments.addFiles(e.dataTransfer.files)
                }
              }}
              leading={composerLeading}
              models={models}
              selectedModel={effectiveModel}
              onModelChange={onModelChange}
              // Live during a turn: a message sent now is QUEUED with the
              // model showing then (useAgentSession.runTurn sends each queued
              // message with its own model and attachment ids), so the
              // picker, the clip and the mic have nothing to protect. Only the
              // host's own switch darkens them. Stop is the interrupt (Adam,
              // 14 Sep: "it just queues till the agent has finished").
              modelDisabled={composerDisabled}
              toolbar={
                <ChatInputToolbar
                  onFilesSelected={files => fileAttachments.addFiles(files)}
                  attachments={fileAttachments.attachments}
                  onRemoveAttachment={fileAttachments.removeFile}
                  isRecording={voiceDictation.isRecording}
                  isTranscribing={voiceDictation.isTranscribing}
                  onToggleRecording={handleToggleRecording}
                  onStopRecording={voiceDictation.stopRecording}
                  onCancelRecording={voiceDictation.cancelRecording}
                  stream={voiceDictation.stream}
                  error={voiceDictation.error}
                  disabled={composerDisabled}
                  micDisabledReason={dictationDisabledReason}
                  micNudge={micNudge}
                />
              }
              chips={composer?.chips}
              onRemoveChip={composer?.onRemoveChip}
              onChipClick={composer?.onChipClick}
              badge={!readOnly ? (
                // The orb, with a one-line status to its left (what the agent
                // and any agents it launched are doing now).
                <Box sx={{ position: 'relative' }}>
                  {statusLine && (
                    <Typography
                      data-testid="composer-status-line"
                      aria-hidden="true"
                      title={statusLine}
                      sx={{
                        position: 'absolute', right: 'calc(100% + 10px)', top: '50%', transform: 'translateY(-50%)',
                        maxWidth: 420, fontSize: 12, color: 'text.secondary', pointerEvents: 'auto',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                    >
                      {statusLine}
                    </Typography>
                  )}
                  <ActivityBadge activity={activity} />
                </Box>
              ) : null}
              textareaRef={textareaRef}
              value={input}
              onChange={setInput}
              onKeyDown={handleKeyDown}
              onPaste={(e: React.ClipboardEvent) => fileAttachments.handlePaste(e.nativeEvent)}
              placeholder={isStreaming && !composerDisabled && props.composerPlaceholder === undefined
                ? 'Type a message to send when this turn finishes...'
                : composerPlaceholder}
              disabled={composerDisabled}
              // While a turn runs the button is Stop until there is something
              // typed to queue; then it is Send. Double-Escape stops either way.
              action={isStreaming && !input.trim() ? 'stop' : 'send'}
              sendDisabled={!input.trim() || composerDisabled}
              onStop={onCancel}
            />
          )}
        </Box>
        </Box>
      </Box>

      {/* Artifact Panel — minimised by default, revealed from the right edge. */}
      {!readOnly && (
        <EdgeReveal
          side="right"
          label="Open artifacts panel"
          icon={<FolderOpenOutlinedIcon />}
          badge={artifacts.length}
          hidden={artifactPanelOpen}
          pulse={artifactPulse}
          onOpen={openArtifactPanel}
        />
      )}
      {!readOnly && (
        <ArtifactPanel
          open={artifactPanelOpen}
          onClose={closeArtifactPanel}
          artifacts={artifacts}
          todos={todos}
          agents={delegatedAgents}
          focusWorkflow={focusWorkflow}
          section={panelSection}
          sessionId={sessionId}
          onPinToDashboard={onPinToDashboard}
          onArtifactClick={(a) => {
            setViewerArtifact(a)
            if (onOpenArtifactViewer) onOpenArtifactViewer(a)
          }}
          onViewAll={() => {
            if (onOpenArtifactViewer) onOpenArtifactViewer(null)
          }}
          apiBaseUrl={apiBaseUrl}
          authHeader={authHeader}
        />
      )}
      {/* One delegated agent's whole conversation, read-only, over the right
          side. Opened from the Agents list or a sub-agent card. */}
      <DelegatedAgentDrawer
        agentRef={openAgentRef}
        messages={messages}
        sessionId={sessionId}
        apiBaseUrl={apiBaseUrl}
        authHeader={authHeader}
        artifacts={artifacts}
        rightOffset={artifactPanelOpen ? ARTIFACT_PANEL_WIDTH : 0}
        onClose={() => setOpenAgentRef(null)}
      />
    </Box>
    </DelegatedAgentsContext.Provider>
  )
}
